import { TextField } from "@mui/material";
import * as React from "react";

import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";

import styles from "./EditableCollectionDescription.module.css";

interface EditableCollectionDescriptionProps {
  description: string;
  collectionId: string;
  orgId: string;
}

export const EditableCollectionDescription: React.FC<
  EditableCollectionDescriptionProps
> = ({ description, collectionId, orgId }) => {
  const [editable, setEditable] = React.useState(false);
  const [current, setCurrent] = React.useState(description);
  const savingRef = React.useRef(false);
  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    setCurrent(description);
  }, [description, collectionId]);

  const { mutateAsync: updateCollection } = useUpdateCollection();

  const save = async (value: string) => {
    if (savingRef.current || cancelledRef.current) {
      return;
    }
    savingRef.current = true;
    const trimmed = value.trim();
    if (trimmed === current) {
      setEditable(false);
      savingRef.current = false;
      return;
    }
    try {
      await updateCollection({
        collectionId,
        resourceOwnerId: orgId,
        updates: { description: trimmed || undefined },
      });
      setCurrent(trimmed);
      setEditable(false);
    } catch {
      // stay in edit mode — error surfaced via mutation.error
    } finally {
      savingRef.current = false;
    }
  };

  if (editable) {
    return (
      <TextField
        autoFocus
        size="small"
        fullWidth
        multiline
        minRows={2}
        maxRows={10}
        defaultValue={current}
        placeholder="No description"
        onBlur={(e) => void save(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            cancelledRef.current = true;
            setEditable(false);
          }
          // Shift+Enter saves; plain Enter inserts a newline (default multiline behavior)
          if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault();
            void save((e.target as HTMLTextAreaElement).value);
          }
        }}
        className={styles.textField}
        inputProps={{
          style: {
            fontSize: "var(--font-size-sm)",
            color: "var(--text-secondary)",
          },
        }}
      />
    );
  }

  return (
    <div
      className={styles.description}
      onClick={() => {
        cancelledRef.current = false;
        setEditable(true);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          cancelledRef.current = false;
          setEditable(true);
        }
      }}
    >
      {current ? <RobotoMarkdown content={current} /> : <i>No description</i>}
    </div>
  );
};
